/**
 * Resolve a document compliance row without treating a same-named form as that document.
 * Forms and documents are separate settings and can share a display name.
 */

export const normalizeComplianceDocumentName = (name) =>
  String(name || "")
    .replace(/\./g, "")
    .trim()
    .toLowerCase();

export const isDocumentComplianceSetting = (setting) => {
  if (!setting || typeof setting !== "object") return false;
  if (setting.type == null || setting.type === "") return true;
  return setting.type === "document";
};

const hasIdentity = (value) => value != null && value !== "";

/**
 * Same compliance row: uuid, then numeric id, then document name.
 * A form is never the same item as a document, even when the names match.
 */
export const sameComplianceIdentity = (setting, item) => {
  if (!isDocumentComplianceSetting(setting) || !item) return false;

  if (hasIdentity(item.uuid) && hasIdentity(setting.uuid)) {
    return String(item.uuid) === String(setting.uuid);
  }

  if (hasIdentity(item.id) && hasIdentity(setting.id)) {
    return Number(item.id) === Number(setting.id);
  }

  const left = normalizeComplianceDocumentName(item.name);
  const right = normalizeComplianceDocumentName(setting.name);
  return left !== "" && left === right;
};

export const findDocumentComplianceSetting = (list, item) => {
  if (!item || !Array.isArray(list)) return null;

  const documents = list.filter(isDocumentComplianceSetting);
  if (documents.length === 0) return null;

  if (hasIdentity(item.uuid)) {
    const byUuid = documents.find(
      (doc) => hasIdentity(doc?.uuid) && String(doc.uuid) === String(item.uuid)
    );
    if (byUuid) return byUuid;
  }

  if (hasIdentity(item.id)) {
    const byId = documents.find(
      (doc) => hasIdentity(doc?.id) && Number(doc.id) === Number(item.id)
    );
    if (byId) return byId;
  }

  const target = normalizeComplianceDocumentName(item.name);
  if (!target) return null;

  return (
    documents.find(
      (doc) => normalizeComplianceDocumentName(doc?.name) === target
    ) || null
  );
};

/**
 * Mandatory comes from the document row itself.
 * When that flag is absent, match only document settings (never a same-named form).
 */
export const isMandatoryDocumentSetting = (documentItem, requiredDocumentslist) => {
  if (!documentItem) return false;

  if (
    Object.prototype.hasOwnProperty.call(documentItem, "is_compliance") &&
    documentItem.is_compliance !== null &&
    documentItem.is_compliance !== undefined
  ) {
    return (
      documentItem.is_compliance === true ||
      documentItem.is_compliance === 1 ||
      documentItem.is_compliance === "1" ||
      documentItem.is_compliance === "true" ||
      documentItem.is_compliance === "yes"
    );
  }

  return (requiredDocumentslist || []).some((requiredDoc) =>
    sameComplianceIdentity(requiredDoc, documentItem)
  );
};
